import { setProperties } from '@ember/object';
import EmberService, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { Service } from 'webapp-types';
import BoardColorConfig, { DefaultColor } from 'webapp/const/board-color-config';
import type { DayOfTheWeek } from 'webapp/enums/enums';
import { AlertEventCondition } from 'webapp/enums/enums';
import { switchDotsAndCommas } from 'webapp/utils/common';

import DsCompare from 'webapp/models-custom/ds-compare';

import type { Stage as AccountProgressStage } from 'webapp/services/account-progress';

interface DayOfTheWeekName {
	value: DayOfTheWeek;
	label: string;
}

interface SpaceMetadataValues {
	businessClassifications: LabelValuePairString[];
	industries: LabelValuePairString[];
	companySizes: LabelValuePairString[];
	annualRevenues: LabelValuePairString[];
}

// constants from BE that carry more than label and value
export interface FormatOption extends LabelValuePairString {
	label3: string; // the format's example
}

export interface BlockTypeOption extends LabelValuePairNumeric {
	label2?: string; // name shown in the block type picker
}

export interface EventConditionOption extends LabelValuePairNumeric {
	isPercent?: boolean;
}

export interface UserLevelOption extends LabelValuePairNumeric {
	text?: string;
}

// BE Timezone::toArray, plus label3 (the offset) set here for the dropdowns
export interface TimezoneOption extends LabelValuePairString {
	validForAccount: boolean;
	gmtOffset: string;
	label3?: string;
}

interface UserMetadataValues {
	departments: {
		value: string;
		label: string;
		roles: LabelValuePairString[];
	}[];
}

export default class ConstantsService extends EmberService {
	@service('account') declare accountService: Service.Account;

	// from BE
	accountProgress: Record<string, { stages: AccountProgressStage[] }> = {};
	accountTypes: LabelValuePairNumeric[] = [];
	actions: LabelValuePairNumeric[] = [];
	aggregates: LabelValuePairNumeric[] = [];
	appstoreUrl = '';
	blockTypes: BlockTypeOption[] = [];
	cdnUrl = '';
	changeFormats: { absolute: LabelValuePairString[]; percent: LabelValuePairString[] } = {
		absolute: [],
		percent: [],
	};
	compares: LabelValuePairNumeric[] = [];
	defaultChangeFormat = '';
	eventActions: LabelValuePairNumeric[] = [];
	eventConditions: EventConditionOption[] = []; // also AlertEventCondition enum
	eventDeliveryTimes: LabelValuePairNumeric[] = [];
	eventDeliveryTypes: LabelValuePairNumeric[] = [];
	formats: FormatOption[] = [];
	googleplayUrl = '';
	granularities: LabelValuePairNumeric[] = [];
	icons: LabelValuePairNumeric[] = [];
	intervals: LabelValuePairNumeric[] = [];
	mkChangeFns: LabelValuePairNumeric[] = [];
	mkTypes: LabelValuePairNumeric[] = [];
	msTypes: LabelValuePairNumeric[] = [];
	notifications: LabelValuePairNumeric[] = [];
	scales: LabelValuePairNumeric[] = [];
	scheduleTypes: LabelValuePairNumeric[] = [];
	tagColors: string[] = [];
	templateCategories: LabelValuePairNumeric[] = [];
	templateTags: LabelValuePairNumeric[] = [];
	userLevels: UserLevelOption[] = [];
	spaceMetadataValues!: SpaceMetadataValues;
	userMetadataValues!: UserMetadataValues;
	forecastHolidaysCountries: LabelValuePairString[] = [];
	aiSummaryLanguages: LabelValuePairString[] = [];
	goalFormatUnits: LabelValuePairString[] = [];
	metricFilterOperators: LabelValuePairString[] = [];
	// end from BE

	defaultChangeFunction = DsCompare.defaultFunction;

	apiMetricBuilderTimeTokens = [
		{ value: '{{now}}', label: 'Now' },
		{ value: '{{0d}}', label: 'Today' },
		{ value: '{{-1d}}', label: 'Yesterday' },
		{ value: '{{-7d}}', label: '7 days ago' },
		{ value: '{{-14d}}', label: '14 days ago' },
		{ value: '{{-28d}}', label: '28 days ago' },
		{ value: '{{-30d}}', label: '30 days ago' },
		{ value: '{{-1m}}', label: '1 month ago' },
		{ value: '{{-3m}}', label: '3 months ago' },
		{ value: '{{-6m}}', label: '6 months ago' },
		{ value: '{{-1y}}', label: '1 year ago' },
		{ value: '{{-2y}}', label: '2 years ago' },
		{ value: '{{fdw}}', label: 'First day of week' },
		{ value: '{{fdm}}', label: 'First day of month' },
		{ value: '{{fdq}}', label: 'First day of quarter' },
		{ value: '{{fdy}}', label: 'First day of year' },
		{ value: '{{ldw}}', label: 'Last day of week' },
		{ value: '{{ldm}}', label: 'Last day of month' },
		{ value: '{{ldq}}', label: 'Last day of quarter' },
		{ value: '{{ldy}}', label: 'Last day of year' },
		{ value: '{{+7d}}', label: '7 days into the future' },
		{ value: '{{+14d}}', label: '14 days into the future' },
		{ value: '{{+1m}}', label: '1 month into the future' },
		{ value: '{{+6m}}', label: '6 months into the future' },
		{ value: '{{+1y}}', label: '1 year in into the future' },
	];

	alertTemplateLayouts: Record<AlertEventCondition, string> = {
		[AlertEventCondition.IS_LESS_THAN]:
			'::metric:: from ::datasource:: ::condition:: ::qualifier:: ::period::.',
		[AlertEventCondition.IS_GREATER_THAN]:
			'::metric:: from ::datasource:: ::condition:: ::qualifier:: ::period::.',
		[AlertEventCondition.DECREASED_BY_MORE_THAN]:
			'::metric:: from ::datasource:: ::condition:: ::value:: ::period::.',
		[AlertEventCondition.INCREASED_BY_MORE_THAN]:
			'::metric:: from ::datasource:: ::condition:: ::value:: ::period::.',
		[AlertEventCondition.PERCENT_DECREASED_BY_MORE_THAN]:
			'::metric:: from ::datasource:: ::condition:: ::value:: ::period::.',
		[AlertEventCondition.PERCENT_INCREASED_BY_MORE_THAN]:
			'::metric:: from ::datasource:: ::condition:: ::value:: ::period::.',
		[AlertEventCondition.INCREASED_OR_DECREASED_BY_MORE_THAN]:
			'::metric:: from ::datasource:: ::condition:: ::value:: ::period::.',
		[AlertEventCondition.PERCENT_INCREASED_OR_DECREASED_BY_MORE_THAN]:
			'::metric:: from ::datasource:: ::condition:: ::value:: ::period::.',
	};

	defaultBoardColor = DefaultColor;

	get boardColors() {
		return BoardColorConfig;
	}

	get hoursOfDay(): LabelValuePairNumeric[] {
		const values: LabelValuePairNumeric[] = [];
		for (let i = 0; i < 24; i++) {
			let tmp = '';
			if (i === 0) {
				tmp = 'midnight';
			} else if (i === 12) {
				tmp = 'noon';
			} else {
				tmp = String(i % 12) + (i < 12 ? ' AM' : ' PM');
			}
			values.push({
				value: i,
				label: tmp,
			});
		}
		return values;
	}

	daysOfWeek = [
		{ value: 1, label: 'Monday' },
		{ value: 2, label: 'Tuesday' },
		{ value: 3, label: 'Wednesday' },
		{ value: 4, label: 'Thursday' },
		{ value: 5, label: 'Friday' },
		{ value: 6, label: 'Saturday' },
		{ value: 0, label: 'Sunday' },
	];

	get daysOfWeekStrings(): LabelValuePairString[] {
		return this.daysOfWeek.map((day) => {
			return {
				value: day.value.toString(),
				label: day.label,
			};
		});
	}

	get daysOfMonth(): LabelValuePairNumeric[] {
		const values: LabelValuePairNumeric[] = [];

		for (let i = 1; i <= 31; i++) {
			values.push({
				value: i,
				label: moment.localeData('en').ordinal(i),
			});
		}

		values.push({
			value: -1,
			label: 'Last day',
		});

		return values;
	}

	get daysOfMonthFiltered(): LabelValuePairNumeric[] {
		return this.daysOfMonth.filter((dayInfo) => {
			return ![29, 30, 31].includes(dayInfo.value);
		});
	}

	get daysOfMonthFilteredForQuarterly(): LabelValuePairNumeric[] {
		const days = [...this.daysOfMonth];

		days.splice(days.length - 1, 0, {
			value: 60,
			label: '60th',
		});

		return days.filter((dayInfo) => {
			return [1, 5, 10, 15, 30, 60, -1].includes(dayInfo.value);
		});
	}

	get currentYear(): number {
		return moment().year();
	}

	@cached
	get europeanFormats(): FormatOption[] {
		// until BE returns US formats, we have to manually replace dots and commas
		// because we also get other units like lbs and minutes in label
		return this.formats.map((format) => {
			return {
				...format,
				label3: switchDotsAndCommas(format.label3),
			};
		});
	}

	get formatOptions() {
		if (this.accountService.isEuropeanNumberFormat) {
			return this.europeanFormats;
		} else {
			return this.formats;
		}
	}

	@cached
	get defaultFormat() {
		return this.formats.find((item) => item.label === 'Auto')?.value || '';
	}

	@tracked private _timezones: TimezoneOption[] = [];
	set timezones(value: TimezoneOption[]) {
		this._timezones = value.map((tz) => {
			return { ...tz, label3: tz.gmtOffset };
		});
	}

	get timezones(): TimezoneOption[] {
		return this._timezones;
	}

	@cached
	get timezonesAvailableForUser(): TimezoneOption[] {
		return this.timezones.filter((item) => item.validForAccount);
	}

	@tracked private _countries: LabelValuePairString[] = [];
	set countries(value: { name: string; dial_code: string; code: string }[]) {
		// parse format from BE into the usual label/value
		this._countries = value.map((c) => {
			return {
				value: c.code,
				label: c.name,
			};
		});
	}

	get countries(): LabelValuePairString[] {
		return this._countries;
	}

	getDayNames(dayOfTheWeek: DayOfTheWeek) {
		const dayName = (
			this.daysOfWeek.find((item) => item.value === dayOfTheWeek) as DayOfTheWeekName
		).label;
		return {
			full: dayName,
			short: dayName.substring(0, 3),
		};
	}

	update(data: object): void {
		setProperties(this, data);
	}
}

declare module '@ember/service' {
	interface Registry {
		constants: ConstantsService;
	}
}
